<template>
  <div>
    <div
      class="fixed inset-0 z-40 bg-gray-900/50 dark:bg-gray-900/80"
      :class="containerClass"
    />
    <div
      ref="modalRef"
      class="fixed top-0 z-50 grid w-full"
      :class="{
        'inset-x-0 h-modal overflow-y-auto overflow-x-hidden md:inset-0 md:h-full': !isSticky,
        'h-full inset-0': isSticky,
        ...(containerClass && { [containerClass]: !!containerClass }),
      }"
      tabindex="0"
      @click.self="clickOutside"
      @keyup.esc="closeWithEsc"
    >
      <div
        :class="`${modalSizeClasses[size]} ${modalPositionClasses[position]}`"
        class="relative w-full p-4"
      >
        <!-- Modal content -->
        <div
          class="relative rounded-lg bg-white shadow-sm dark:bg-gray-700"
          :class="{ 'flex flex-col max-h-[calc(100vh-2rem)]': isSticky }"
        >
          <!-- Modal header -->
          <slot
            name="header-container"
            :persistent="persistent"
            :show-close-button="showCloseButton"
            :close-modal="closeModal"
          >
            <div class="flex items-center justify-between rounded-t text-lg font-semibold text-black px-8 pt-8 pb-1">
              <slot name="header" />

              <button
                v-if="!persistent || showCloseButton"
                aria-label="close"
                class="ms-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-black hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white border-0"
                type="button"
                @click="closeModal"
              >
                <slot name="close-icon">
                  <IconSvg
                    name="flowbite:close-outline"
                    class="w-5! h-5!"
                  />
                </slot>
              </button>
            </div>
          </slot>
          <!-- Modal body -->
          <div :class="{ 'flex-1 overflow-auto': isSticky }">
            <slot name="body" />
          </div>
          <!-- Modal footer -->
          <slot name="footer-container">
            <div
              v-if="$slots.footer"
              class="rounded-b border-t border-gray-200 px-8 pb-8 dark:border-gray-600"
            >
              <slot name="footer" />
            </div>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, type Ref } from 'vue';
import type { ModalSize, ModalPosition } from '@/types/modal';
import IconSvg from '@base-shared/components/IconSvg.vue';

type ExtendedModalSize = ModalSize | '4.5xl';

interface ModalProps {
  notEscapable?: boolean;
  persistent?: boolean;
  size?: ExtendedModalSize;
  position?: ModalPosition;
  showCloseButton?: boolean;
  isSticky?: boolean;
  containerClass?: string;
}

const props = withDefaults(defineProps<ModalProps>(), {
  notEscapable: false,
  persistent: false,
  size: '2xl',
  position: 'center',
  showCloseButton: false,
  isSticky: false
});

const emit = defineEmits(['close', 'click:outside']);
const modalSizeClasses: Record<ExtendedModalSize, string> = {
  'xs': 'max-w-xs',
  'sm': 'max-w-sm',
  'md': 'max-w-md',
  'lg': 'max-w-lg',
  'xl': 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '4.5xl': 'max-w-[58rem]',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl'
};

const modalPositionClasses: Record<ModalPosition, string> = {
  'top-start': 'self-start justify-self-start',
  'top-center': 'self-start justify-self-center',
  'top-end': 'self-start justify-self-end',
  'center-start': 'self-center justify-self-start',
  'center': 'self-center justify-self-center',
  'center-end': 'self-center justify-self-end',
  'bottom-start': 'self-end justify-self-start',
  'bottom-center': 'self-end justify-self-center',
  'bottom-end': 'self-end justify-self-end'
};

function closeModal() {
  emit('close');
}

function clickOutside() {
  if (!props.persistent) {
    emit('click:outside');
    closeModal();
  }
}

function closeWithEsc() {
  if (!props.notEscapable && !props.persistent) closeModal();
}
const modalRef: Ref<HTMLElement | null> = ref(null);
onMounted(() => {
  if (modalRef.value) {
    modalRef.value.focus();
  }
});
</script>
